import { useMemo } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import SpeechBubble from './SpeechBubble';
import {
  getTranscriptionLineTags,
  NO_TRANSCRIPTION_MESSAGE,
  NO_SPEECH_DETECTED_TRANSCRIPT_MESSAGE,
} from './transcription.util';

interface BubbleChatProps {
  transcriptLines: string[];
}

const BubbleChat = ({ transcriptLines }: BubbleChatProps) => {
  const theme = useTheme();
  // Extract unique speakers from transcript lines
  const speakers = useMemo(() => {
    const uniqueSpeakers: string[] = [];
    transcriptLines.forEach(line => {
      const { name } = getTranscriptionLineTags(line);
      if (name && !uniqueSpeakers.includes(name)) {
        uniqueSpeakers.push(name);
      }
    });
    return uniqueSpeakers;
  }, [transcriptLines]);

  if (transcriptLines.length === 0) {
    return (
      <Typography variant="bodyLarge" component="p">
        No transcript available. Please wait for the transcript to be generated.
      </Typography>
    );
  }

  if (
    transcriptLines[0] === NO_TRANSCRIPTION_MESSAGE ||
    transcriptLines[0] === NO_SPEECH_DETECTED_TRANSCRIPT_MESSAGE
  ) {
    return (
      <Box sx={{ width: '100%' }}>
        <Box
          sx={{
            width: 'fit-content',
            padding: '8px 24px',
            backgroundColor: theme.palette.neutralAccent.main,
            borderRadius: 0.5,
            border: `1px solid ${theme.palette.greyShades[250]}`,
          }}
        >
          <Typography variant="bodyLarge" component="p">
            {transcriptLines[0]}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {transcriptLines.map((line, index) => {
        const { name, time, text } = getTranscriptionLineTags(line);

        const speakerIndex = name ? speakers.indexOf(name) : 0;

        return (
          <SpeechBubble
            key={`transcript-bubble-${index}`}
            name={name}
            time={time}
            text={text}
            anchor={speakerIndex % 2 === 0 ? 'left' : 'right'}
          />
        );
      })}
    </Box>
  );
};

export default BubbleChat;
